export const TIMESTAMP_QUERY_PARAM = "_t_";

/**
 * Reloads the current page without using the browser cache.
 *
 * This function adds a timestamp to the URL's search parameters to ensure
 * that the browser fetches a fresh version of the page from the server.
 *
 * The timestamp is set to the current time in milliseconds since the Unix epoch.
 *
 * @example
 * # Call this function to reload the page without cache
 * reloadPageWithoutCache();
 */
export function reloadPageWithoutCache(): void {
  const url = currentUrlWithTimestamp();

  window.location.replace(url);
}

/**
 * Forces the current URL to have a timestamp parameter.
 *
 * This function adds a timestamp to the URL's search parameters to ensure
 * that the browser fetches a fresh version of the page from the server.
 */
export function forceUrlToHaveTimestamp(): void {
  addTimestampToUrl();
  setInterval(addTimestampToUrl, 100);
  window.addEventListener("popstate", addTimestampToUrl);
}

/**
 * Generates a URL with a timestamp parameter for cache-busting purposes.
 *
 * This function takes the current URL and adds or updates a `${TIMESTAMP_QUERY_PARAM}`  query parameter
 * with the current time in milliseconds. It preserves any existing query parameters
 * and the URL hash if present.
 *
 * @returns {string} The current URL with an updated timestamp parameter
 *
 * @example
 * # If current URL is "https://example.com/page?param=value#section"
 * const url = currentUrlWithTimestamp();
 * # Returns something like "https://example.com/page?param=value&_t_=1234567890#section"
 */
export function currentUrlWithTimestamp(): string {
  const searchParams = new URLSearchParams(window.location.search);

  searchParams.set(TIMESTAMP_QUERY_PARAM, Date.now().toString());

  const urlHash = window.location.hash ? `#${window.location.hash}` : "";

  return `${window.location.pathname}?${searchParams.toString()}${urlHash}`;
}

/**
 * Checks if the current URL has a timestamp parameter.
 *
 * @returns {boolean} True if the URL has a timestamp parameter, false otherwise
 */
export function currentUrlHasTimestamp(): boolean {
  const searchParams = new URLSearchParams(window.location.search);

  const timestamp = searchParams.get(TIMESTAMP_QUERY_PARAM);

  return !!timestamp && Number.isInteger(Number(timestamp));
}

/**
 * Adds a timestamp parameter to the current URL if it doesn't already have one.
 *
 * This function ensures that the URL has a `${TIMESTAMP_QUERY_PARAM}` query parameter by adding it if necessary.
 * It also updates the URL in the browser's history to reflect the new state.
 */
export function addTimestampToUrl(): void {
  if (currentUrlHasTimestamp()) {
    return;
  }

  history.replaceState(history.state, "", currentUrlWithTimestamp());
}
